import { Severity } from '@sentry/browser';

export interface SentryConfig {
  dsn: string;
  enabled: boolean;
}

export enum SentryEnvironment {
  PRODUCTION = 'production',
  REVIEW = 'review',
  STAGE = 'stage',
  DEVELOPMENT = 'development',
}

export const PAYMENT = 'payment';

export enum SentryTag {
  ENV = 'env',
  USER_ID = 'userId',
  LEVEL = 'level',
  MODULE = 'module',
  URL = 'url',
}

// HTTP-данные для секции Request в UI Sentry.
export interface SentryRequestData {
  method?: string;
  url?: string;
  query_string?: Record<string, string> | string;
}

export interface SentryLogPayload {
  module: string;
  level: Severity;
  message: string;
  user?: { id: string | number };
  tags?: Record<string, string | number | boolean | undefined>;
  body?: Record<string, unknown>;
  request?: SentryRequestData;
  fingerprint?: string[];
}

// Минимальный контракт Sentry Scope — только методы, используемые в applyTags и capture.
export type SentryScope = {
  setLevel(level: Severity): void;
  setTag(key: string, value: string): void;
  setUser(user: { id: string } | null): void;
  setExtras(extras: Record<string, unknown>): void;
  setFingerprint(fingerprint: string[]): void;
};

// Заголовки шагов — идут в тег payment_step, fingerprint и в квадратных скобках тайтла.
export enum PaymentStep {
  PAY_INIT = 'Инициализация платежа',
  FREE_PAYMENT_SUCCESS = 'Покупка',
  PAY_SUCCESS = 'Подтверждение оплаты',
  ACCESS_NOT_GRANTED = 'Выдача доступа',
  PAY_FAIL_CALLBACK = 'Обработка отказа',
  PAY_COMPLETE = 'Завершение транзакции',
  WIDGET_NOT_LOADED = 'Загрузка виджета',
  INVOICE_CHECK = 'Проверка оплаты инвойса',
}

// Fallback-текст тайтла при ошибке, если в payment/error нет message.
export const PAYMENT_TITLE: Record<PaymentStep, string> = {
  [PaymentStep.PAY_INIT]: 'Ошибка запроса pay',
  [PaymentStep.FREE_PAYMENT_SUCCESS]: 'Промокод на 100%, доступ не выдан',
  [PaymentStep.PAY_SUCCESS]: 'Ошибка запроса pay-success',
  [PaymentStep.ACCESS_NOT_GRANTED]: 'Ошибка запроса pay-success',
  [PaymentStep.PAY_FAIL_CALLBACK]: 'Платёж не выполнен',
  [PaymentStep.PAY_COMPLETE]: 'Ошибка запроса pay-complete',
  [PaymentStep.WIDGET_NOT_LOADED]: 'Скрипт CloudPayments не загружен',
  [PaymentStep.INVOICE_CHECK]: 'pay-check не подтверждён или прерван',
};

// Fallback-текст тайтла при успехе, если в payment нет message.
export const PAYMENT_SUCCESS_TITLE: Record<PaymentStep, string> = {
  [PaymentStep.PAY_INIT]: '',
  [PaymentStep.FREE_PAYMENT_SUCCESS]: 'Промокод на 100%, доступ выдан',
  [PaymentStep.PAY_SUCCESS]: 'pay-success выполнен',
  [PaymentStep.ACCESS_NOT_GRANTED]: '',
  [PaymentStep.PAY_FAIL_CALLBACK]: '',
  [PaymentStep.PAY_COMPLETE]: 'pay-complete выполнен',
  [PaymentStep.WIDGET_NOT_LOADED]: '',
  [PaymentStep.INVOICE_CHECK]: 'pay-check подтверждён',
};

export enum PaymentTag {
  STEP = 'payment_step',
  INVOICE = 'invoice',
  HAS_PROMO = 'has_promo',
  PRODUCT_ID = 'product_id',
  PROMO_CODE = 'promo_code',
}

export interface PaymentLogContext {
  request?: SentryRequestData;
  hasPromoCode?: boolean;
  productId?: number;
  promoCode?: string;
  invoiceUuid?: string;
  isFree?: boolean;
}

export interface PaymentFailOptions {
  error?: unknown;
  payment?: unknown;
}

// Минимальный shape ответа CloudPayments / платёжного API.
export interface PaymentResponse {
  message?: string;
  reason?: string;
  code?: string | number;
  result?: {
    message?: string;
    code?: number;
  };
}

// Минимальный shape HTTP-ошибки (Angular HttpErrorResponse).
export interface SentryHttpError {
  status?: number;
  url?: string;
  error?: unknown;
  message?: string;
}
